import { isValidElement, type ReactNode } from 'react'

/*
  Sorting and filtering every table by its columns (owner, 2026-10-24: "Ensure all tabular
  lists are sortable by column and filterable by column"). TableWrap reads the table it wraps:
  each cell's text is taken from what the cell renders (its strings and numbers, an input's
  value, and the values handed to a component such as Fields), then a column sorts as numbers,
  dates or words, whichever its cells are, and filters on the text a person sees.

  How a table is filtered (owner, 2026-10-27: the filter box under each heading was "klunky",
  hard to understand with no dropdown): one search over every column, and a dropdown for each
  column whose cells are a few values repeated (a pasture's kind, a cow's class). Numbers,
  dates and names are searched and sorted, not picked from a list of every value.
*/

// Props that carry wiring, not what is shown.
const SKIP =
  /^(className|style|id|key|ref|href|to|type|scope|testId|data-.*|aria-.*|on[A-Z].*|.*PublicId|publicId|title)$/

function propsText(value: unknown, depth: number): string[] {
  if (depth > 3 || value === null || value === undefined || typeof value === 'boolean') return []
  if (typeof value === 'string' || typeof value === 'number') return [String(value)]
  if (isValidElement(value)) return [textOf(value)]
  if (Array.isArray(value)) return value.flatMap((v) => propsText(v, depth + 1))
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([k]) => !SKIP.test(k))
      .flatMap(([, v]) => propsText(v, depth + 1))
  }
  return []
}

function selectedText(props: Record<string, unknown>): string {
  const chosen = String(props.value ?? props.defaultValue ?? '')
  const options: Record<string, unknown>[] = []
  const walk = (n: ReactNode) => {
    if (Array.isArray(n)) n.forEach(walk)
    else if (isValidElement(n)) {
      const p = n.props as Record<string, unknown>
      if (n.type === 'option') options.push(p)
      else walk(p.children as ReactNode)
    }
  }
  walk(props.children as ReactNode)
  const option = options.find((o) => String(o.value ?? textOf(o.children as ReactNode)) === chosen)
  return option ? textOf(option.children as ReactNode) : chosen
}

/** The text a cell shows, as near as can be told without rendering it. */
export function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOf).filter(Boolean).join(' ').replace(/\s+/g, ' ')
  if (!isValidElement(node)) return ''
  const props = node.props as Record<string, unknown>
  if (typeof node.type === 'string') {
    if (node.type === 'input' || node.type === 'textarea')
      return String(props.value ?? props.defaultValue ?? '')
    // A select shows its chosen option, not every option it holds (the draft grazes).
    if (node.type === 'select') return selectedText(props)
    return textOf(props.children as ReactNode)
  }
  // A component (Fields, a Link): what it was given to show.
  return propsText(props, 0).filter(Boolean).join(' ')
}

export type SortKey = { kind: 0; n: number } | { kind: 1; s: string } | null

const NUMBER = /^[-−]?\$?[\d,]*\.?\d+\s*(%|lb|ac|acres|head|hd)?$/i
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const SHOWN_DAY = /^([A-Z][a-z]{2}) (\d{1,2}), (\d{4})/
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/

/**
 * A date as written (Mar 1, 2019 or 2019-03-01) as one number, yyyymmdd: the ranch's calendar
 * day as shown, never turned into an instant through UTC or the browser's zone.
 */
export function dayOf(text: string): number | null {
  const shown = SHOWN_DAY.exec(text)
  if (shown) {
    const m = MONTHS.indexOf(shown[1]!)
    return m < 0 ? null : Number(shown[3]) * 10000 + (m + 1) * 100 + Number(shown[2])
  }
  const iso = ISO_DAY.exec(text)
  return iso ? Number(iso[1]) * 10000 + Number(iso[2]) * 100 + Number(iso[3]) : null
}

/** A cell as something to sort by: a number (money, counts, dates as time) or words. */
export function sortKey(text: string): SortKey {
  const t = text.trim()
  if (!t || t === '—' || t === '-') return null
  if (NUMBER.test(t)) {
    const n = Number(t.replace(/[$,%\s]|lb|ac|acres|head|hd/gi, '').replace('−', '-'))
    if (Number.isFinite(n)) return { kind: 0, n }
  }
  const day = dayOf(t)
  if (day !== null) return { kind: 0, n: day }
  return { kind: 1, s: t }
}

const collator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'base' })

/** Ascending; an empty cell goes last whichever way the column is turned (as the cattle list). */
export function compareKeys(a: SortKey, b: SortKey, descending: boolean): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1
  let c: number
  if (a.kind === 0 && b.kind === 0) c = a.n - b.n
  else if (a.kind === 1 && b.kind === 1) c = collator.compare(a.s, b.s)
  else c = a.kind - b.kind
  return descending ? -c : c
}

export interface TableSort {
  column: number
  descending: boolean
}

/** A click on a column: sort by it, then turn it, then back to the order it came in. */
export function nextTableSort(sort: TableSort | null, column: number): TableSort | null {
  if (!sort || sort.column !== column) return { column, descending: false }
  return sort.descending ? null : { column, descending: true }
}

/** What a table is narrowed to: words anywhere in a row, and a value picked for some columns. */
export interface TableFilter {
  search: string
  /** By column; '' is every value. */
  picks: string[]
}

export const NO_TABLE_FILTER: TableFilter = { search: '', picks: [] }

export function isTableFiltered(f: TableFilter): boolean {
  return !!f.search.trim() || f.picks.some(Boolean)
}

/** The rows (as their cells' texts) that pass the filter, in the sorted order. */
export function arrange(rows: string[][], filter: TableFilter, sort: TableSort | null): number[] {
  const words = filter.search.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const kept = rows
    .map((cells, i) => ({ cells, i, all: cells.join(' ').toLowerCase() }))
    .filter(
      ({ cells, all }) =>
        filter.picks.every((p, c) => !p || (cells[c] ?? '').trim() === p) &&
        words.every((w) => all.includes(w)),
    )
  if (!sort) return kept.map((r) => r.i)
  return kept
    .map((r) => ({ ...r, key: sortKey(r.cells[sort.column] ?? '') }))
    .sort((x, y) => compareKeys(x.key, y.key, sort.descending) || x.i - y.i)
    .map((r) => r.i)
}

/*
  When a column earns a dropdown: its cells are short words that repeat, between two and a
  dozen of them. A name column (every cell different) or a number column would make a dropdown
  as long as the table, which is what the search is for.
*/
const MOST_CHOICES = 12
const LONGEST_CHOICE = 32

/** The values a column's dropdown offers, in order, or null when the column should not have one. */
export function choicesOf(rows: string[][], column: number): string[] | null {
  const texts = rows.map((r) => (r[column] ?? '').trim()).filter((t) => t && t !== '—')
  const values = [...new Set(texts)]
  if (values.length < 2 || values.length > MOST_CHOICES) return null
  if (values.length > Math.ceil((rows.length * 2) / 3)) return null
  if (values.some((v) => v.length > LONGEST_CHOICE)) return null
  if (values.every((v) => sortKey(v)?.kind === 0)) return null
  return values.sort(collator.compare)
}
